/**
 * Secure Storage Utility
 * Provides secure token storage with AES-GCM encryption and httpOnly cookie support
 *
 * STORAGE STRATEGY:
 * - Primary: localStorage (persists across tabs and browser sessions)
 * - Fallback: sessionStorage (if localStorage is unavailable)
 * - Optional: httpOnly cookies (requires server support)
 *
 * ENCRYPTION - TWO API VERSIONS:
 *
 * 1. LEGACY SYNC API (Backward Compatible):
 *    - getToken(), setToken(), getUserData(), etc.
 *    - Uses base64 encoding (NOT cryptographically secure)
 *    - Kept for backward compatibility with existing code
 *    - Will show security warning in console
 *
 * 2. NEW ASYNC API (Secure):
 *    - getTokenAsync(), setTokenAsync(), getUserDataAsync(), etc.
 *    - Uses Web Crypto API with AES-GCM 256-bit encryption
 *    - Recommended for new code
 *    - Automatically migrates old data to strong encryption
 *
 * MIGRATION STRATEGY:
 * - Existing code continues to work with sync API (legacy encryption)
 * - New code should use async API (strong encryption)
 * - Data automatically upgrades to strong encryption when using async API
 * - Old data remains readable by both APIs
 *
 * SECURITY NOTE:
 * - Even with strong encryption, data is in localStorage (vulnerable to XSS)
 * - For maximum security, use httpOnly cookies (requires backend support)
 *
 * This allows users to stay logged in across multiple tabs,
 * which is the expected behavior for modern web applications.
 */

import cryptoHelper from './cryptoHelper';

interface SecureStorageOptions {
  encrypt?: boolean;
  useHttpOnlyFallback?: boolean;
  expirationMinutes?: number;
}

class SecureStorage {
  private readonly ENCRYPTION_KEY = 'pms-secure-key'; // Legacy key for backward compatibility
  private readonly COOKIE_NAME = 'pms-auth-token';
  private readonly USER_COOKIE_NAME = 'pms-user-data';
  private migrationComplete = false;
  private useCryptoAPI = false;
  private securityWarningShown = false;

  constructor() {
    // Check if Web Crypto API is available
    this.useCryptoAPI = cryptoHelper.isAvailable();

    if (!this.useCryptoAPI) {
      console.warn('⚠️ [SECURITY] Web Crypto API not available, using fallback encryption');
    }

    // Clean up any corrupted data first, then migrate
    this.cleanupCorruptedData();
    this.migrateFromSessionStorage();
  }

  /**
   * Show security warning once per session
   */
  private showSecurityWarning(): void {
    if (!this.securityWarningShown && this.useCryptoAPI) {
      console.warn(
        '⚠️ [SECURITY WARNING] Using legacy sync API with weak encryption.\n' +
        'Consider migrating to async API:\n' +
        '  - Use getTokenAsync() instead of getToken()\n' +
        '  - Use setTokenAsync() instead of setToken()\n' +
        'See documentation for migration guide.'
      );
      this.securityWarningShown = true;
    }
  }

  /**
   * Encrypt data using Web Crypto API (AES-GCM) or fallback to legacy method
   * Returns a Promise that resolves to encrypted string
   */
  private async encrypt(data: string): Promise<string> {
    try {
      if (this.useCryptoAPI) {
        // Use real cryptographic encryption
        return await cryptoHelper.encrypt(data);
      } else {
        // Fallback to legacy base64 (with warning)
        console.warn('⚠️ [SECURITY] Using legacy encryption (base64), not cryptographically secure');
        return this.encryptLegacy(data);
      }
    } catch (error) {
      console.error('Encryption failed, using fallback:', error);
      return this.encryptLegacy(data);
    }
  }

  /**
   * Legacy encryption using btoa/atob with key mixing
   * WARNING: This is basic obfuscation, not cryptographic security
   * Only used as fallback when Web Crypto API is unavailable
   */
  private encryptLegacy(data: string): string {
    try {
      const keyedData = `${this.ENCRYPTION_KEY}:${data}`;
      return btoa(keyedData);
    } catch (error) {
      console.error('Legacy encryption failed:', error);
      return data;
    }
  }

  /**
   * Decrypt data - handles Web Crypto API, legacy base64, and plain JSON formats
   * Automatically detects format and decrypts accordingly
   */
  private async decrypt(encryptedData: string): Promise<string> {
    // First, check if data is already valid JSON (unencrypted legacy data)
    try {
      JSON.parse(encryptedData);
      return encryptedData;
    } catch {
      // Not plain JSON, continue with decryption
    }

    // Check if it's Web Crypto API encrypted data (longer, random-looking base64)
    if (this.useCryptoAPI && !cryptoHelper.isLegacyEncryption(encryptedData)) {
      try {
        return await cryptoHelper.decrypt(encryptedData);
      } catch (error) {
        console.warn('⚠️ [STORAGE] Web Crypto decryption failed, trying legacy:', error);
        // Fall through to legacy decryption
      }
    }

    // Try legacy base64 decryption
    try {
      const decoded = atob(encryptedData);
      if (decoded.startsWith(`${this.ENCRYPTION_KEY}:`)) {
        const decrypted = decoded.substring(this.ENCRYPTION_KEY.length + 1);
        // Auto-migrate: re-encrypt with Web Crypto API on next save
        return decrypted;
      }
      // Decoded but doesn't have our prefix - might be legacy data
      try {
        JSON.parse(decoded);
        return decoded;
      } catch {
        console.warn('⚠️ [STORAGE] Data decoded but not valid JSON, treating as corrupted');
        return '';
      }
    } catch (error) {
      console.error('❌ [STORAGE] Decryption failed:', error);
      return '';
    }
  }

  /**
   * Check if a string looks like it might be valid data (not corrupted)
   */
  private isValidStoredData(data: string | null): boolean {
    if (!data) return false;
    
    // Check if it's valid JSON directly
    try {
      JSON.parse(data);
      return true;
    } catch {
      // Not direct JSON, might be encrypted
    }

    // Check if it's valid base64 that decrypts to valid JSON
    try {
      const decoded = atob(data);
      if (decoded.startsWith(`${this.ENCRYPTION_KEY}:`)) {
        const decrypted = decoded.substring(this.ENCRYPTION_KEY.length + 1);
        JSON.parse(decrypted);
        return true;
      }
    } catch {
      // Invalid
    }

    return false;
  }

  /**
   * Clean up any corrupted data in storage
   */
  private cleanupCorruptedData(): void {
    try {
      // Check localStorage for corrupted user data
      const localUser = localStorage.getItem('user');
      if (localUser && !this.isValidStoredData(localUser)) {
        console.warn('⚠️ [STORAGE] Removing corrupted user data from localStorage');
        localStorage.removeItem('user');
      }

      // Check sessionStorage for corrupted user data
      const sessionUser = sessionStorage.getItem('user');
      if (sessionUser && !this.isValidStoredData(sessionUser)) {
        console.warn('⚠️ [STORAGE] Removing corrupted user data from sessionStorage');
        sessionStorage.removeItem('user');
      }
    } catch (error) {
      console.error('❌ [STORAGE] Cleanup failed:', error);
    }
  }

  /**
   * Set secure cookie with httpOnly attribute (requires server support)
   */
  private setCookie(name: string, value: string, options: SecureStorageOptions = {}) {
    const expirationMinutes = options.expirationMinutes || 480; // 8 hours default
    const expires = new Date(Date.now() + expirationMinutes * 60 * 1000);
    
    const cookieOptions = [
      `${name}=${value}`,
      `expires=${expires.toUTCString()}`,
      'path=/',
      'SameSite=Strict'
    ];

    // Add Secure flag for HTTPS
    if (window.location.protocol === 'https:') {
      cookieOptions.push('Secure');
    }

    document.cookie = cookieOptions.join('; ');
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  private deleteCookie(name: string) {
    const expiredCookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Max-Age=0; path=/;`;

    // Remove the current host cookie.
    document.cookie = expiredCookie;

    // Older stage/prod deployments may have left a parent-domain cookie that
    // is visible on both allroomie.com and stage.allroomie.com. Cookie deletion
    // must use the same Domain attribute that created it.
    const hostname = window.location.hostname.toLowerCase();
    if (hostname === 'allroomie.com' || hostname.endsWith('.allroomie.com')) {
      document.cookie = `${expiredCookie} Domain=allroomie.com;`;
    }
  }

  /**
   * Migrate existing sessions from sessionStorage to localStorage
   * This ensures users who were logged in with the old system stay logged in
   */
  private migrateFromSessionStorage(): void {
    if (this.migrationComplete) return;
    
    try {
      // Check if there's data in sessionStorage that needs to be migrated
      const sessionToken = sessionStorage.getItem('authToken');
      const sessionUser = sessionStorage.getItem('user');
      const sessionRefreshToken = sessionStorage.getItem('refreshToken');
      
      // Only migrate if sessionStorage has VALID data AND localStorage doesn't
      if (sessionToken && !localStorage.getItem('authToken')) {
        console.log('🔄 [STORAGE] Migrating auth token from sessionStorage to localStorage');
        localStorage.setItem('authToken', sessionToken);
      }
      
      if (sessionUser && this.isValidStoredData(sessionUser) && !localStorage.getItem('user')) {
        console.log('🔄 [STORAGE] Migrating user data from sessionStorage to localStorage');
        localStorage.setItem('user', sessionUser);
      }
      
      if (sessionRefreshToken && !localStorage.getItem('refreshToken')) {
        console.log('🔄 [STORAGE] Migrating refresh token from sessionStorage to localStorage');
        localStorage.setItem('refreshToken', sessionRefreshToken);
      }
      
      // Clear sessionStorage after successful migration to avoid duplicate data
      if (sessionToken || sessionUser || sessionRefreshToken) {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('refreshToken');
        console.log('✅ [STORAGE] Migration complete - cleared sessionStorage');
      }
    } catch (error) {
      console.error('❌ [STORAGE] Migration failed:', error);
    }
    
    this.migrationComplete = true;
  }

  // ============================================================================
  // LEGACY SYNC API - Backward Compatible (uses base64, NOT secure)
  // ============================================================================

  /**
   * Store authentication token (LEGACY SYNC VERSION - uses weak encryption)
   * @deprecated Use setTokenAsync() for strong encryption
   */
  setToken(token: string, options: SecureStorageOptions = {}): void {
    this.showSecurityWarning();
    const encryptedToken = options.encrypt !== false ? this.encryptLegacy(token) : token;

    if (options.useHttpOnlyFallback) {
      this.setCookie(this.COOKIE_NAME, encryptedToken, options);
    }

    try {
      localStorage.setItem('authToken', encryptedToken);
    } catch (error) {
      console.warn('⚠️ [STORAGE] localStorage unavailable, using sessionStorage');
      sessionStorage.setItem('authToken', encryptedToken);
    }
  }

  /**
   * Retrieve authentication token (LEGACY SYNC VERSION)
   * @deprecated Use getTokenAsync() for better security
   */
  getToken(): string | null {
    this.showSecurityWarning();
    let token = this.getCookie(this.COOKIE_NAME);

    if (!token) {
      try {
        token = localStorage.getItem('authToken');
      } catch (error) {
        token = sessionStorage.getItem('authToken');
      }
    }

    if (token) {
      // Synchronous decryption (legacy base64 only)
      try {
        const decoded = atob(token);
        if (decoded.startsWith(`${this.ENCRYPTION_KEY}:`)) {
          return decoded.substring(this.ENCRYPTION_KEY.length + 1);
        }
        // Try parsing as plain JSON
        JSON.parse(decoded);
        return decoded;
      } catch {
        // If not base64 or not JSON, check if it's plain JSON string
        try {
          JSON.parse(token);
          return token;
        } catch {
          console.warn('⚠️ [STORAGE] Token data corrupted, clearing...');
          this.clearAll();
          return null;
        }
      }
    }

    return null;
  }

  /**
   * Store user data (LEGACY SYNC VERSION - uses weak encryption)
   * @deprecated Use setUserDataAsync() for strong encryption
   */
  setUserData(userData: any, options: SecureStorageOptions = {}): void {
    this.showSecurityWarning();
    const userString = JSON.stringify(userData);
    const encryptedData = options.encrypt !== false ? this.encryptLegacy(userString) : userString;

    if (options.useHttpOnlyFallback) {
      this.setCookie(this.USER_COOKIE_NAME, encryptedData, options);
    }

    try {
      localStorage.setItem('user', encryptedData);
    } catch (error) {
      console.warn('⚠️ [STORAGE] localStorage unavailable, using sessionStorage', error);
      sessionStorage.setItem('user', encryptedData);
    }
  }

  /**
   * Retrieve user data (LEGACY SYNC VERSION)
   * @deprecated Use getUserDataAsync() for better security
   */
  getUserData(): any | null {
    this.showSecurityWarning();
    let userData = this.getCookie(this.USER_COOKIE_NAME);

    if (!userData) {
      try {
        userData = localStorage.getItem('user');
      } catch (error) {
        userData = sessionStorage.getItem('user');
      }
    }

    if (userData) {
      try {
        // Synchronous decryption (legacy base64 only)
        let decryptedData: string;
        try {
          const decoded = atob(userData);
          if (decoded.startsWith(`${this.ENCRYPTION_KEY}:`)) {
            decryptedData = decoded.substring(this.ENCRYPTION_KEY.length + 1);
          } else {
            decryptedData = decoded;
          }
        } catch {
          // Not base64, try as plain JSON
          decryptedData = userData;
        }

        const parsedData = JSON.parse(decryptedData);
        return parsedData;
      } catch (error) {
        console.error('❌ [STORAGE] Failed to parse user data, clearing corrupted data. Error:', error);
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        this.deleteCookie(this.USER_COOKIE_NAME);
        return null;
      }
    }

    return null;
  }

  /**
   * Store profile data (LEGACY SYNC VERSION - uses weak encryption)
   * @deprecated Use setProfileDataAsync() for strong encryption
   */
  setProfileData(profileData: any, options: SecureStorageOptions = {}): void {
    this.showSecurityWarning();
    const minimalProfile = {
      // Include id for B-side actions (e.g., booking cancel payload).
      // This is low-sensitivity compared to the auth token and avoids refetching profile repeatedly.
      id: profileData?.id ?? null,
      firstName: profileData?.firstName || null,
      lastName: profileData?.lastName || null,
      nickname: profileData?.nickname || null,
      avatar: profileData?.avatar || null,
      email: profileData?.email || null,
      phone: profileData?.phone || null,
    };

    const profileString = JSON.stringify(minimalProfile);
    const encryptedData = options.encrypt !== false ? this.encryptLegacy(profileString) : profileString;

    try {
      localStorage.setItem('user_profile', encryptedData);
    } catch (error) {
      console.warn('⚠️ [STORAGE] Failed to cache profile data:', error);
    }
  }

  /**
   * Retrieve profile data (LEGACY SYNC VERSION)
   * @deprecated Use getProfileDataAsync() for better security
   */
  getProfileData(): any | null {
    this.showSecurityWarning();
    try {
      const profileData = localStorage.getItem('user_profile');

      if (profileData) {
        let decryptedData: string;
        try {
          const decoded = atob(profileData);
          if (decoded.startsWith(`${this.ENCRYPTION_KEY}:`)) {
            decryptedData = decoded.substring(this.ENCRYPTION_KEY.length + 1);
          } else {
            decryptedData = decoded;
          }
        } catch {
          decryptedData = profileData;
        }

        const parsedData = JSON.parse(decryptedData);
        return parsedData;
      }
    } catch (error) {
      console.error('❌ [STORAGE] Failed to retrieve profile data:', error);
      localStorage.removeItem('user_profile');
    }

    return null;
  }

  /**
   * Check if token exists (LEGACY SYNC VERSION)
   * @deprecated Use hasTokenAsync() for better security
   */
  hasToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Check if user data exists (LEGACY SYNC VERSION)
   * @deprecated Use hasUserDataAsync() for better security
   */
  hasUserData(): boolean {
    return this.getUserData() !== null;
  }

  /**
   * Store generic data (LEGACY SYNC VERSION - uses weak encryption)
   * @deprecated Use setItemAsync() for strong encryption
   */
  setItem(key: string, value: string, options: SecureStorageOptions = {}): void {
    this.showSecurityWarning();
    const encryptedValue = options.encrypt !== false ? this.encryptLegacy(value) : value;

    try {
      localStorage.setItem(key, encryptedValue);
    } catch (error) {
      sessionStorage.setItem(key, encryptedValue);
    }
  }

  /**
   * Retrieve generic data (LEGACY SYNC VERSION)
   * @deprecated Use getItemAsync() for better security
   */
  getItem(key: string): string | null {
    this.showSecurityWarning();
    let value: string | null = null;

    try {
      value = localStorage.getItem(key);
    } catch (error) {
      value = sessionStorage.getItem(key);
    }

    if (value) {
      try {
        const decoded = atob(value);
        if (decoded.startsWith(`${this.ENCRYPTION_KEY}:`)) {
          return decoded.substring(this.ENCRYPTION_KEY.length + 1);
        }
        return decoded;
      } catch {
        return value;
      }
    }

    return null;
  }

  // ============================================================================
  // NEW ASYNC API - Secure (uses Web Crypto API with AES-GCM)
  // ============================================================================

  /**
   * Store authentication token with strong encryption (ASYNC VERSION - RECOMMENDED)
   * Uses Web Crypto API with AES-GCM 256-bit encryption
   */
  async setTokenAsync(token: string, options: SecureStorageOptions = {}): Promise<void> {
    const encryptedToken = options.encrypt !== false ? await this.encrypt(token) : token;

    if (options.useHttpOnlyFallback) {
      this.setCookie(this.COOKIE_NAME, encryptedToken, options);
    }

    try {
      localStorage.setItem('authToken', encryptedToken);
    } catch (error) {
      console.warn('⚠️ [STORAGE] localStorage unavailable, using sessionStorage');
      sessionStorage.setItem('authToken', encryptedToken);
    }
  }

  /**
   * Retrieve authentication token with strong decryption (ASYNC VERSION - RECOMMENDED)
   * Automatically handles legacy data and migrates to strong encryption
   */
  async getTokenAsync(): Promise<string | null> {
    let token = this.getCookie(this.COOKIE_NAME);

    if (!token) {
      try {
        token = localStorage.getItem('authToken');
      } catch (error) {
        token = sessionStorage.getItem('authToken');
      }
    }

    if (token) {
      const decrypted = await this.decrypt(token);
      if (!decrypted) {
        console.warn('⚠️ [STORAGE] Token data corrupted, clearing...');
        this.clearAll();
        return null;
      }
      return decrypted;
    }

    return null;
  }

  /**
   * Store user data with strong encryption (ASYNC VERSION - RECOMMENDED)
   */
  async setUserDataAsync(userData: any, options: SecureStorageOptions = {}): Promise<void> {
    const userString = JSON.stringify(userData);
    const encryptedData = options.encrypt !== false ? await this.encrypt(userString) : userString;

    if (options.useHttpOnlyFallback) {
      this.setCookie(this.USER_COOKIE_NAME, encryptedData, options);
    }

    try {
      localStorage.setItem('user', encryptedData);
    } catch (error) {
      console.warn('⚠️ [STORAGE] localStorage unavailable, using sessionStorage', error);
      sessionStorage.setItem('user', encryptedData);
    }
  }

  /**
   * Retrieve user data with strong decryption (ASYNC VERSION - RECOMMENDED)
   */
  async getUserDataAsync(): Promise<any | null> {
    let userData = this.getCookie(this.USER_COOKIE_NAME);

    if (!userData) {
      try {
        userData = localStorage.getItem('user');
      } catch (error) {
        userData = sessionStorage.getItem('user');
      }
    }

    if (userData) {
      try {
        const decryptedData = await this.decrypt(userData);

        if (!decryptedData) {
          console.warn('⚠️ [STORAGE] User data corrupted, clearing...');
          localStorage.removeItem('user');
          sessionStorage.removeItem('user');
          return null;
        }

        const parsedData = JSON.parse(decryptedData);
        return parsedData;
      } catch (error) {
        console.error('❌ [STORAGE] Failed to parse user data, clearing corrupted data. Error:', error);
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        this.deleteCookie(this.USER_COOKIE_NAME);
        return null;
      }
    }

    return null;
  }

  /**
   * Store profile data with strong encryption (ASYNC VERSION - RECOMMENDED)
   */
  async setProfileDataAsync(profileData: any, options: SecureStorageOptions = {}): Promise<void> {
    const minimalProfile = {
      firstName: profileData?.firstName || null,
      lastName: profileData?.lastName || null,
      nickname: profileData?.nickname || null,
      avatar: profileData?.avatar || null,
      email: profileData?.email || null,
      phone: profileData?.phone || null,
    };

    const profileString = JSON.stringify(minimalProfile);
    const encryptedData = options.encrypt !== false ? await this.encrypt(profileString) : profileString;

    try {
      localStorage.setItem('user_profile', encryptedData);
    } catch (error) {
      console.warn('⚠️ [STORAGE] Failed to cache profile data:', error);
    }
  }

  /**
   * Retrieve profile data with strong decryption (ASYNC VERSION - RECOMMENDED)
   */
  async getProfileDataAsync(): Promise<any | null> {
    try {
      const profileData = localStorage.getItem('user_profile');

      if (profileData) {
        const decryptedData = await this.decrypt(profileData);

        if (!decryptedData) {
          console.warn('⚠️ [STORAGE] Profile data corrupted, clearing...');
          localStorage.removeItem('user_profile');
          return null;
        }

        const parsedData = JSON.parse(decryptedData);
        return parsedData;
      }
    } catch (error) {
      console.error('❌ [STORAGE] Failed to retrieve profile data:', error);
      localStorage.removeItem('user_profile');
    }

    return null;
  }

  /**
   * Check if token exists (ASYNC VERSION - RECOMMENDED)
   */
  async hasTokenAsync(): Promise<boolean> {
    return (await this.getTokenAsync()) !== null;
  }

  /**
   * Check if user data exists (ASYNC VERSION - RECOMMENDED)
   */
  async hasUserDataAsync(): Promise<boolean> {
    return (await this.getUserDataAsync()) !== null;
  }

  /**
   * Store generic data with strong encryption (ASYNC VERSION - RECOMMENDED)
   */
  async setItemAsync(key: string, value: string, options: SecureStorageOptions = {}): Promise<void> {
    const encryptedValue = options.encrypt !== false ? await this.encrypt(value) : value;

    try {
      localStorage.setItem(key, encryptedValue);
    } catch (error) {
      sessionStorage.setItem(key, encryptedValue);
    }
  }

  /**
   * Retrieve generic data with strong decryption (ASYNC VERSION - RECOMMENDED)
   */
  async getItemAsync(key: string): Promise<string | null> {
    let value: string | null = null;

    try {
      value = localStorage.getItem(key);
    } catch (error) {
      value = sessionStorage.getItem(key);
    }

    if (value) {
      const decrypted = await this.decrypt(value);
      return decrypted || null;
    }

    return null;
  }

  // ============================================================================
  // COMMON METHODS (no encryption involved)
  // ============================================================================

  /**
   * Clear all stored authentication data
   * Clears ALL storage locations to ensure complete logout
   */
  clearAll(): void {
    // Clear cookies
    this.deleteCookie(this.COOKIE_NAME);
    this.deleteCookie(this.USER_COOKIE_NAME);

    // Clear localStorage (primary)
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('user_profile');
      localStorage.removeItem('refreshToken');
    } catch (error) {
      // LocalStorage not available
    }

    // Clear sessionStorage (legacy/fallback)
    try {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('refreshToken');
    } catch (error) {
      // SessionStorage not available
    }

    console.log('🧹 [STORAGE] Cleared all authentication data');
  }

  /**
   * Remove generic data
   * Removes from ALL storage locations
   */
  removeItem(key: string): void {
    // Remove from localStorage (primary)
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // LocalStorage not available
    }

    // Remove from sessionStorage (legacy/fallback)
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // SessionStorage not available
    }
  }
}

const secureStorage = new SecureStorage();
export default secureStorage;
